// App-wide internationalisation. Portuguese (pt) is the source language: every
// pt string is the original UI copy and must stay byte-for-byte identical.
//
// Resources are bundled synchronously, so `t()` is usable right after this
// module is imported (see src/index.js, which imports it before <App />).
// See ./README.md for the conventions every screen follows.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";

import pt from "./locales/pt";
import en from "./locales/en";
import es from "./locales/es";

export const LANGUAGE_STORAGE_KEY = "ginastee-language";
export const DEFAULT_LANGUAGE = "pt";

// Labels are the language's own name on purpose (a picker should read naturally
// to the person who needs it), so they are not translated. `keywords` feeds the
// language picker's search so "spanish", "espanhol" or "es-es" all find Español.
export const LANGUAGES = [
  {
    code: "pt",
    label: "Português",
    short: "PT",
    intl: "pt-BR",
    dayjs: "pt-br",
    keywords: "portugues portuguese brasil brazil pt-br",
  },
  {
    code: "en",
    label: "English",
    short: "EN",
    intl: "en-US",
    dayjs: "en",
    keywords: "ingles inglês english united states usa eua en-us",
  },
  {
    code: "es",
    label: "Español",
    short: "ES",
    intl: "es-ES",
    dayjs: "es",
    keywords: "espanhol espanol spanish es-es",
  },
];

export function isSupportedLanguage(code) {
  return LANGUAGES.some((l) => l.code === code);
}

function readStoredLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
}

/** Current language code: "pt" | "en" | "es". */
export function getLanguage() {
  return isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

export function getLanguageMeta(code = getLanguage()) {
  return LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
}

/**
 * BCP-47 locale for Intl / toLocaleDateString / toLocaleString calls
 * ("pt-BR" | "en-US" | "es-ES"). Use this instead of a hardcoded 'pt-BR'
 * whenever a date or number is rendered for the user.
 */
export function getIntlLocale() {
  return getLanguageMeta().intl;
}

function applyLanguageSideEffects(code) {
  const meta = getLanguageMeta(code);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, meta.code);
  } catch (e) {
    /* storage unavailable (private mode) — language still applies in-session */
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = meta.intl.toLowerCase();
  }
  dayjs.locale(meta.dayjs);
}

i18n.use(initReactI18next).init({
  resources: { pt, en, es },
  lng: readStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES.map((l) => l.code),
  ns: Object.keys(pt),
  defaultNS: "common",
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  react: { useSuspense: false },
});

applyLanguageSideEffects(i18n.language);
i18n.on("languageChanged", applyLanguageSideEffects);

/** Switches the UI language. Resolves once every subscribed component re-rendered. */
export function setLanguage(code) {
  if (!isSupportedLanguage(code) || code === i18n.language) return Promise.resolve();
  return i18n.changeLanguage(code);
}

/** Plain `t` for non-React code (helpers, enums, hooks' side effects). Call it lazily, never at module top level. */
export const t = i18n.t.bind(i18n);

export default i18n;
